import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  Grid,
  IconButton,
} from '@mui/material';
import { Close, Send } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, EMAILJS_PUBLIC_KEY } from '../config';

const AppointmentForm = ({ open, onClose, type = 'appointment', googleSheetUrl }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    duration: '',
    purpose: '',
    message: '',
    pickupLocation: '',
    dropLocation: '',
    materialType: '',
    weight: '',
    // Stock Market specific
    investmentGoal: '',
    portfolioSize: '',
    experienceLevel: '',
    // Interior specific
    carpetArea: '',
    budgetEstimate: '',
    location: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const timeSlots = [
    '09:00 AM',
    '10:00 AM',
    '11:00 AM',
    '12:00 PM',
    '01:00 PM',
    '02:00 PM',
    '03:00 PM',
    '04:00 PM',
    '05:00 PM',
    '06:00 PM',
  ];

  const durationOptions = [
    '30 minutes',
    '1 hour',
    '1.5 hours',
    '2 hours',
  ];

  const purposeOptions = type === 'appointment'
    ? [
      'Comprehensive Stock Market Training',
      'Portfolio Management & Health Check',
      'Technical & Fundamental Analysis Service',
      'Wealth Management Consultation',
      'One-on-One Mentorship',
      'Other Specialist Request',
    ]
    : type === 'logistics'
      ? [
        'Industrial Goods',
        'Construction Material',
        'FMCG (Consumer Goods)',
        'Electronics',
        'Household/Relocation',
        'Pharmaceuticals',
        'Other',
      ]
      : [
        'Luxury Residential Interior',
        'Commercial Office Space Design',
        'Single Room / Apartment Makeover',
        'Modular Kitchen & Wardrobes',
        'Turnkey Interior Solutions',
        'Other Interior Request',
      ];

  const consignmentTypes = [
    'Full Truck Load (FTL)',
    'Part Truck Load (PTL)',
    'Local Distribution',
    'Long-Haul Transportation',
    'Parcel/Express',
  ];

  const experienceLevels = [
    'Beginner (No prior knowledge)',
    'Intermediate (Some trading experience)',
    'Advanced (Active investor/trader)',
  ];

  const budgetRanges = [
    'Below 5 Lakhs',
    '5 - 15 Lakhs',
    '15 - 30 Lakhs',
    'Above 30 Lakhs',
    'Custom / Premium Project',
  ];

  const portfolioSizes = [
    'Upto 5 Lakhs',
    '5 - 25 Lakhs',
    '25 - 50 Lakhs',
    '50 Lakhs +',
    'HNI / Institutional',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newMessage = formData.message;

    const currentData = { ...formData, [name]: value };

    // Auto-prefill message based on context selections if message is empty or auto-generated
    const isAutoGenerated = !formData.message ||
      formData.message.startsWith("I'd like to book") ||
      formData.message.startsWith("I'm looking to book") ||
      formData.message.startsWith("I'm interested in");

    if (isAutoGenerated) {
      if (type === 'appointment' && (name === 'purpose' || name === 'experienceLevel')) {
        if (currentData.purpose) {
          newMessage = `I'd like to book a session for ${currentData.purpose}.`;
          if (currentData.experienceLevel) newMessage += ` I have ${currentData.experienceLevel} background.`;
          newMessage += ` Please share details.`;
        }
      } else if (type === 'logistics' && (name === 'purpose' || name === 'materialType')) {
        if (currentData.purpose || currentData.materialType) {
          newMessage = `I'm looking to book a truck.`;
          if (currentData.materialType) newMessage += ` Need to transport ${currentData.materialType}.`;
          if (currentData.purpose) newMessage += ` Consignment: ${currentData.purpose}.`;
          newMessage += ` Please provide a quote.`;
        }
      } else if (type === 'interior' && (name === 'purpose' || name === 'location')) {
        if (currentData.purpose) {
          newMessage = `I'm interested in ${currentData.purpose} for my property.`;
          if (currentData.location) newMessage += ` At ${currentData.location}.`;
          newMessage += ` I'd like to discuss design possibilities.`;
        }
      }
    }

    setFormData({
      ...formData,
      [name]: value,
      message: newMessage,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate form
    if (!formData.name || !formData.email || !formData.phone || !formData.preferredDate || !formData.preferredTime) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Phone validation
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.phone.replace(/\D/g, ''))) {
      setError('Please enter a valid 10-digit phone number');
      setLoading(false);
      return;
    }

    try {
      const timestamp = new Date().toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        dateStyle: 'short',
        timeStyle: 'short',
      });

      if (!googleSheetUrl) {
        setError('Google Sheet URL not configured. Please contact administrator.');
        setLoading(false);
        return;
      }

      // 1. Submit to Google Sheets (Parallel)
      if (googleSheetUrl) {
        fetch(googleSheetUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            timestamp,
            type: type === 'appointment' ? 'Stock Market Appointment' : type === 'logistics' ? 'Logistics & Truck Booking' : 'Interior Design Consultation',
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            preferredDate: formData.preferredDate,
            preferredTime: formData.preferredTime,
            duration: formData.duration || 'N/A',
            purpose: formData.purpose || 'N/A',
            message: formData.message || 'N/A',
            pickupLocation: formData.pickupLocation || 'N/A',
            dropLocation: formData.dropLocation || 'N/A',
            materialType: formData.materialType || 'N/A',
            weight: formData.weight || 'N/A',
            investmentGoal: formData.investmentGoal || 'N/A',
            portfolioSize: formData.portfolioSize || 'N/A',
            experienceLevel: formData.experienceLevel || 'N/A',
            carpetArea: formData.carpetArea || 'N/A',
            budgetEstimate: formData.budgetEstimate || 'N/A',
            location: formData.location || 'N/A',
          }),
        }).catch(err => console.error('Google Sheet submission failed:', err));
      }

      // 2. Send Automated Email using EmailJS (Manual check for window.emailjs)
      if (window.emailjs) {
        try {
          window.emailjs.init(EMAILJS_PUBLIC_KEY);
          const serviceId = EMAILJS_SERVICE_ID;
          const templateId = EMAILJS_TEMPLATE_ID;

          await window.emailjs.send(serviceId, templateId, {
            from_name: formData.name,
            from_email: formData.email,
            phone: formData.phone,
            service_type: type === 'appointment' ? 'Equity Guidance' : type === 'logistics' ? 'Swift Logistics' : 'Interior Consultation',
            preferred_date: formData.preferredDate,
            preferred_time: formData.preferredTime,
            purpose: formData.purpose || 'N/A',
            message: formData.message || 'N/A',
            pickup_location: formData.pickupLocation || 'N/A',
            drop_location: formData.dropLocation || 'N/A',
            material_type: formData.materialType || 'N/A',
            weight: formData.weight || 'N/A',
            investment_goal: formData.investmentGoal || 'N/A',
            portfolio_size: formData.portfolioSize || 'N/A',
            experience_level: formData.experienceLevel || 'N/A',
            carpet_area: formData.carpetArea || 'N/A',
            budget_estimate: formData.budgetEstimate || 'N/A',
            location: formData.location || 'N/A',
            to_email: 'paushgroup@gmail.com', // Target email
          });
        } catch (mailErr) {
          console.error('EmailJS failed:', mailErr);
        }
      }

      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        preferredDate: '',
        preferredTime: '',
        duration: '',
        purpose: '',
        message: '',
        pickupLocation: '',
        dropLocation: '',
        materialType: '',
        weight: '',
        investmentGoal: '',
        portfolioSize: '',
        experienceLevel: '',
        carpetArea: '',
        budgetEstimate: '',
        location: '',
      });

      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError('Failed to submit form. Please try again or contact us directly.');
      console.error('Form submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        preferredDate: '',
        preferredTime: '',
        duration: '',
        purpose: '',
        message: '',
        pickupLocation: '',
        dropLocation: '',
        materialType: '',
        weight: '',
      });
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  const getMinDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1); // Minimum tomorrow
    return today.toISOString().split('T')[0];
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'var(--bg-primary)',
          borderRadius: 4,
          boxShadow: 'var(--shadow-lg)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
          }}
        >
          {type === 'appointment' ? 'Book Appointment' : type === 'logistics' ? 'Book a Truck' : 'Book Consultation'}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: 'var(--text-secondary)',
            '&:hover': { color: 'var(--primary)', background: 'rgba(0, 0, 0, 0.05)' },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <Alert
              severity="success"
              sx={{
                m: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': { color: 'var(--primary)' },
              }}
            >
              We've received your request! Our team will reach out shortly.
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{
          p: { xs: 2.5, md: 3 },
          pt: { xs: 1.5, md: 1 },
          maxHeight: '85vh',
          overflowY: 'auto',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: '0.85rem' }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={{ xs: 2, md: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                name="name"
                label="Full Name"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={textFieldStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                name="email"
                label="Email Address"
                placeholder="john@example.com"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                sx={textFieldStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                name="phone"
                label="Phone Number"
                placeholder="9876543210"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
                inputProps={{ maxLength: 10 }}
                sx={textFieldStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                name="preferredDate"
                label={type === 'logistics' ? "Pickup Date" : "Select Date"}
                type="date"
                value={formData.preferredDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: getMinDate() }}
                sx={textFieldStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                name="preferredTime"
                label="Select Time"
                select
                value={formData.preferredTime}
                onChange={handleChange}
                fullWidth
                sx={textFieldStyles}
              >
                {timeSlots.map((time) => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {type === 'logistics' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    name="pickupLocation"
                    label="Pickup Address / City"
                    placeholder="Enter pickup location"
                    value={formData.pickupLocation}
                    onChange={handleChange}
                    fullWidth
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    name="dropLocation"
                    label="Drop Address / City"
                    placeholder="Enter drop location"
                    value={formData.dropLocation}
                    onChange={handleChange}
                    fullWidth
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    name="materialType"
                    label="Material Type"
                    select
                    value={formData.materialType}
                    onChange={handleChange}
                    fullWidth
                    sx={textFieldStyles}
                  >
                    {purposeOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    name="weight"
                    label="Approx Weight (approx Kg/Ton)"
                    placeholder="e.g. 500 Kg or 10 Tons"
                    value={formData.weight}
                    onChange={handleChange}
                    fullWidth
                    sx={textFieldStyles}
                  />
                </Grid>
              </>
            )}

            {type === 'appointment' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    name="experienceLevel"
                    label="Trading Experience"
                    select
                    value={formData.experienceLevel}
                    onChange={handleChange}
                    fullWidth
                    sx={textFieldStyles}
                  >
                    {experienceLevels.map((lvl) => (
                      <MenuItem key={lvl} value={lvl}>
                        {lvl}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    name="portfolioSize"
                    label="Portfolio / Capital Size"
                    select
                    value={formData.portfolioSize}
                    onChange={handleChange}
                    fullWidth
                    sx={textFieldStyles}
                  >
                    {portfolioSizes.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="investmentGoal"
                    label="Primary Investment Goal"
                    placeholder="e.g. Long-term wealth, Regular Income, Learning"
                    value={formData.investmentGoal}
                    onChange={handleChange}
                    fullWidth
                    sx={textFieldStyles}
                  />
                </Grid>
              </>
            )}

            {type === 'interior' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    name="carpetArea"
                    label="Approx Area (Sq. Ft.)"
                    placeholder="e.g. 1500 sqft"
                    value={formData.carpetArea}
                    onChange={handleChange}
                    fullWidth
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    name="location"
                    label="Project Location"
                    placeholder="City / Area"
                    value={formData.location}
                    onChange={handleChange}
                    fullWidth
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    name="budgetEstimate"
                    label="Estimated Budget"
                    select
                    value={formData.budgetEstimate}
                    onChange={handleChange}
                    fullWidth
                    sx={textFieldStyles}
                  >
                    {budgetRanges.map((range) => (
                      <MenuItem key={range} value={range}>
                        {range}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                name="purpose"
                label={type === 'logistics' ? "Consignment Type" : "Service Type"}
                select
                value={formData.purpose}
                onChange={handleChange}
                fullWidth
                sx={textFieldStyles}
              >
                {(type === 'logistics' ? consignmentTypes : purposeOptions).map((purpose) => (
                  <MenuItem key={purpose} value={purpose}>
                    {purpose}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="message"
                label="Additional Details"
                placeholder="How can we help you?"
                multiline
                rows={3}
                value={formData.message}
                onChange={handleChange}
                fullWidth
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            sx={{
              color: 'var(--text-secondary)',
              fontWeight: 600,
              '&:hover': { color: 'var(--text-primary)' },
            }}
          >
            Discard
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            endIcon={loading ? <CircularProgress size={18} /> : <Send />}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              boxShadow: 'var(--shadow-soft)',
            }}
          >
            {loading ? 'Sending...' : 'Confirm'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(0, 71, 171, 0.3)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--primary)', borderWidth: 2 },
    height: { md: 45 },
    '& input[type="date"]': {
      paddingTop: { md: '10px' },
      paddingBottom: { md: '10px' },
    }
  },
  '& .MuiInputLabel-root': {
    color: 'var(--text-secondary)',
    fontSize: { md: '0.85rem' },
    transform: { md: 'translate(14px, 11px) scale(1)' },
    '&.MuiInputLabel-shrink': {
      transform: { md: 'translate(14px, -8px) scale(0.75)' },
      backgroundColor: '#fff',
      padding: '0 8px',
      marginLeft: '-4px',
    }
  },
};

export default AppointmentForm;
